import React, {
	useLayoutEffect,
	useEffect,
	useContext,
	useState,
	useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragOverlay
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy
} from '@dnd-kit/sortable';

import * as S from './styles';
import { color } from 'styles/pallete';
import { listRoutes } from 'router/listRoutes';
import LoadGeneralContext from 'context/LoadGeneralContext';
import TitleNavBarContext from 'context/TitleNavBarContext';
import SystemNotifyContext from 'context/SystemNotifyContext';
import { UseTranslationProps } from 'types/UseTranslationProps';
import DynamicPageOptionsList from 'components/DynamicPageOptionsList';
import MobileDynamicPage from 'components/MobileDynamicPage';
import DynamicPageContext from 'context/DynamicPage';
import { ItemDynamicPageInPhone } from 'types/ItemDynamicPageInPhone';
import { generateUid } from 'utils/functions';
import FormDynamicPageSelected from 'components/FormDynamicPageSelected';
import { TypeSlug } from 'types/ElementDynamicOptionProps';
import { getOptionList } from './reqs';

interface DraggingItemOriginProps {
	origem: string;
	id: string;
	slug: TypeSlug;
}

interface DraggingItemDestinyProps {
	destiny: string;
	id: string;
	slug: TypeSlug;
}

export default function DynamicPageSelected() {
	const lastDraggedId = useRef<string | null>(null);

	const navigate = useNavigate();

	const { i18n }: UseTranslationProps = useTranslation();
	const { handleNotify } = useContext(SystemNotifyContext);
	const { setIsOpenLoad, setMessage } = useContext(LoadGeneralContext);
	const { setDataAbouTitle } = useContext(TitleNavBarContext);
	const {
		dynamicPageSelected,
		listOptions,
		itensOnPhone,
		setListOptions,
		setItensOnPhone,
		backupListOptions,
		isFormOpen,
		setIsFormOpen,
		setTypeItemToAdd,
		setBackupLisOptions,
		setModuleInSavingMode,
		setModuleOnPhoneToEdit,
		moduleInSavingMode
	} = useContext(DynamicPageContext);

	// States para o drag and drop
	const [activeId, setActiveId] = useState<string | null>(null);
	const [draggingElement, setDraggingElement] = useState<null | any>(null);
	const [draggingItemOrigin, setdraggingItemOrigin] =
		useState<null | DraggingItemOriginProps>(null);
	const [draggingItemDestiny, setdraggingItemDestiny] =
		useState<null | DraggingItemDestinyProps>(null);

	// Configuração dos sensores
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates
		})
	);

	function generateNewListOptions() {
		const newOptions = backupListOptions.map((item) => {
			return {
				...item,
				id: generateUid()
			};
		});
		setListOptions(newOptions);
	}

	//verifica se pode ou não abrir o formulário
	function verifyIfOpenForm(item: { data: any; id: string; react: any }) {
		if (
			draggingItemOrigin?.origem === 'listOptions' &&
			draggingItemDestiny?.destiny === 'itensOnPhone'
		) {
			const { id } = item;

			itensOnPhone.forEach((item) => {
				if (item.id === id) {
					setIsFormOpen(true);
				}
			});
		}
	}

	function findItemOnListOptions(id: string): TypeSlug {
		const item = listOptions.filter((item) => item.id === id);
		if (item.length > 0) {
			return item[0].slug;
		}

		return 'notFound';
	}

	// Função para começar o arraste
	function handleDragStart(event: any) {
		const { active } = event;

		setActiveId(active.id);
		setdraggingItemOrigin({
			id: active.id,
			origem: active?.data?.current?.sortable?.containerId || 'none',
			slug: findItemOnListOptions(active.id)
		});

		const activeElement = document.querySelector(`[data-id="${active.id}"]`);
		if (activeElement) {
			setDraggingElement(activeElement.outerHTML);
		}
	}

	// Função para quando o arraste terminar
	function handleDragEnd(event: any) {
		const { active, over } = event;

		setActiveId(null); // Resetando o estado
		setDraggingElement(null); // Resetando o estado

		// Verificando de qual lista o item está
		const activeContainer = listOptions.some((item) => item.id === active.id)
			? 'listOptions'
			: 'itensOnPhone';
		const overContainer = listOptions.some((item) => item?.id === over?.id)
			? 'listOptions'
			: 'itensOnPhone';

		setTypeItemToAdd(draggingItemOrigin?.slug || 'notFound');

		if (!over || active.id === over.id) {
			generateNewListOptions(); // Resetando a lista de opções
			verifyIfOpenForm(active);
			return;
		}

		if (activeContainer === overContainer) {
			generateNewListOptions(); // Resetando a lista de opções
			verifyIfOpenForm(active);

			// Movimento dentro da mesma lista
			if (activeContainer === 'listOptions') {
				setListOptions((prevItems) => {
					const oldIndex = prevItems.findIndex((item) => item.id === active.id);
					const newIndex = prevItems.findIndex((item) => item.id === over.id);
					return arrayMove(prevItems, oldIndex, newIndex);
				});
			} else {
				setItensOnPhone((prevItems) => {
					const oldIndex = prevItems.findIndex((item) => item.id === active.id);
					const newIndex = prevItems.findIndex((item) => item.id === over.id);
					return arrayMove(prevItems, oldIndex, newIndex);
				});
			}
		} else {
			// Movimento entre listas diferentes
			generateNewListOptions(); // Resetando a lista de opções
			verifyIfOpenForm(active);

			if (activeContainer === 'listOptions') {
				// Mover de listOptions para itensOnPhone
				setListOptions((prevItems) =>
					prevItems.filter((item) => item.id !== active.id)
				);

				setItensOnPhone((prevItems) => {
					const newIndex = prevItems.findIndex((item) => item.id === over.id);
					const updatedItems = [...prevItems];

					const newIteOnphone: ItemDynamicPageInPhone = {
						id: generateUid(),
						data: {},
						isEdit: false,
						slug: active.slug,
						vtexData: [],
						__typename: 'Component'
					};

					if (newIndex === -1) {
						updatedItems.push({ ...newIteOnphone, isEdit: false }); // Adicionar item com campo `isEdit` como false
					} else {
						updatedItems.splice(newIndex, 0, {
							...newIteOnphone,
							isEdit: false
						});
					}
					return updatedItems;
				});
			}
		}

		//caso o usuário tente colocar na lista do celular um novo item e depois tire o item de cima do celular, ele tirar o item antes colocado na lista
		if (
			draggingItemOrigin?.origem === draggingItemDestiny?.destiny &&
			moduleInSavingMode !== null
		) {
			const newList = itensOnPhone.filter(
				(item) => item.id !== draggingItemOrigin?.id
			);

			setItensOnPhone(newList);
		}
	}

	// Função para o movimento de arraste entre as listas
	function handleDragOver(event: any) {
		const { active, over } = event;
		if (!over) return;

		lastDraggedId.current = active.id; //evita repetir alguns trechos do código mais de 1 vez, por que essa função é disparada várias vezes no dragg

		setdraggingItemDestiny({
			id: over.id,
			destiny: over?.data?.current?.sortable?.containerId || 'none',
			slug: 'notFound'
		});

		const activeContainer = listOptions.some((item) => item.id === active.id)
			? 'listOptions'
			: 'itensOnPhone';
		const overContainer = listOptions.some((item) => item.id === over.id)
			? 'listOptions'
			: 'itensOnPhone';

		// Verifique se os containers são diferentes
		if (activeContainer !== overContainer) {
			if (
				activeContainer === 'listOptions' &&
				overContainer === 'itensOnPhone'
			) {
				// Movendo de listOptions para itensOnPhone
				setListOptions((prevItems) =>
					prevItems.filter((item) => item.id !== active.id)
				);

				const newIteOnphone: ItemDynamicPageInPhone = {
					id: active.id,
					data: {},
					isEdit: false,
					slug:
						active?.data?.current?.slug ||
						draggingItemOrigin?.slug ||
						'notFound',
					vtexData: [],
					__typename: 'Component'
				};

				// Aguarde a atualização do estado antes de adicionar um novo item
				setItensOnPhone((prevItems) => {
					const updatedItems = [...prevItems, newIteOnphone].filter(Boolean); // Remove undefineds
					return updatedItems;
				});

				if (lastDraggedId.current === active.id) {
					setTimeout(() => {
						const copyObj = structuredClone(newIteOnphone); // Criação de um novo objeto independente
						setModuleInSavingMode(copyObj);
						setModuleOnPhoneToEdit(copyObj);
					}, 50); // Pequeno delay para evitar estado inconsistente
				}
			}
		}
	}

	// Função para resetar o estado
	function handleDragCancel() {
		setActiveId(null);
		setDraggingElement(null);
	}

	//busca as opções de módulos do app
	useEffect(() => {
		// Fetch data from API
		(async () => {
			setIsOpenLoad(true);
			setMessage('Buscando componentes');
			const { data, isSuccess } = await getOptionList(handleNotify);
			if (isSuccess && data) {
				setListOptions(data);
				setBackupLisOptions(data);
			}

			setIsOpenLoad(false);
		})();
	}, []);

	// Muda o título do componente
	useLayoutEffect(() => {
		setDataAbouTitle((value) => ({ ...value, title: 'dynamicPage.title' }));
	}, [i18n?.language]);

	useEffect(() => {
		if (dynamicPageSelected === null) {
			navigate(listRoutes.dynamicPages);
		}
	}, [dynamicPageSelected, navigate]);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
			onDragCancel={handleDragCancel}
			onDragOver={handleDragOver}
		>
			<S.container>
				<SortableContext
					id="listOptions"
					items={listOptions}
					strategy={verticalListSortingStrategy}
				>
					<DynamicPageOptionsList />
				</SortableContext>

				<SortableContext
					id="itensOnPhone"
					items={itensOnPhone}
					strategy={verticalListSortingStrategy}
				>
					<MobileDynamicPage isDragging={activeId === null ? false : true} />
				</SortableContext>

				<DragOverlay>
					{activeId && draggingElement && (
						<div
							dangerouslySetInnerHTML={{ __html: draggingElement }}
							style={{
								pointerEvents: 'none',
								backgroundColor: color.interface.onDragColor,
								cursor: 'grabbing',
								padding: '10px',
								borderRadius: '8px',
								boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)'
							}}
						/>
					)}
				</DragOverlay>

				{isFormOpen && <FormDynamicPageSelected />}
			</S.container>
		</DndContext>
	);
}
